import { useState, useRef, useEffect, useCallback } from "react";
import { MODAL_TYPE } from "../../constants";
import Modal from "../modal/Modal";
import Button from "../buttons/button/Button";
import { BUTTON_TYPES } from "../buttons/button/ButtonTypes";
import useEventBus from "../../hooks/useEventBus";
import { useMySizeProfile } from "../../context/mySizeProfile/MySizeProfileContext";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { logError, ERROR_TYPE } from "../../utils/newRelicLogger";
import styles from "./SizeHelpModal.scss";

export default function SizeHelpModal() {
  const [open, setOpen] = useState<boolean>(false);
  const focusOnCloseRef = useRef<HTMLElement | null>(null);

  const {
    mySizeProfile,
    saveMySizeProfile,
    loading,
    isEligible,
    formState,
    updateFormState,
    brands: contextBrands,
    legLengths: contextLegLengths,
    loadBrands,
    restoreFormStateFromProfile,
    dismissMySizeProfile,
  } = useMySizeProfile();
  type RuntimeConstants = {
    getTranslation: (key: string) => string;
    siteDomain?: string;
  };
  const { getTranslation, siteDomain } =
    useRuntimeConstants() as RuntimeConstants;

  const trustedMessageOrigins = useCallback(() => {
    const origins = new Set<string>([window.location.origin]);

    if (!siteDomain) {
      return origins;
    }

    try {
      origins.add(new URL(siteDomain, window.location.origin).origin);
    } catch {
      // no-op
    }

    return origins;
  }, [siteDomain]);

  useEventBus(
    "sizeHelpOpen",
    async ({
      focusOnClose,
    }: {
      focusOnClose?: HTMLElement | null;
    } = {}) => {
      focusOnCloseRef.current = focusOnClose ?? null;
      setOpen(true);
      await loadBrands();
      await restoreFormStateFromProfile();
    },
    [isEligible, loadBrands, restoreFormStateFromProfile],
  );

  const closeModal = useCallback(() => {
    dismissMySizeProfile();
    setOpen(false);
  }, [dismissMySizeProfile]);

  const handleSelectBrand = (brandName: string) => {
    const brand = contextBrands.find((b) => b.brandName === brandName);
    updateFormState({
      selectedBrand: brandName,
      brandId: brand?.brandId ?? null,
      brandSize: "",
      legLengthValue: "",
    });
  };

  const handleCreateSizeProfile = async () => {
    if (loading) {
      return;
    }

    if (
      !formState.selectedBrand ||
      !formState.brandSize ||
      !selectedBrandData
    ) {
      return;
    }
    const selectedMapping = selectedBrandData.sizeMappings.find(
      (sm) => sm.brandSize === formState.brandSize,
    );

    if (!selectedMapping || !selectedMapping.sizeProfileId) {
      return;
    }

    const sizeProfileId = selectedMapping.sizeProfileId as string;

    const legLength = formState.legLengthValue
      ? Number(formState.legLengthValue)
      : null;

    try {
      const formStateToSave = {
        selectedBrand: formState.selectedBrand as string,
        brandId: selectedBrandData.brandId,
        brandSize: formState.brandSize,
        legLengthValue: formState.legLengthValue,
      };

      await saveMySizeProfile(sizeProfileId, legLength, formStateToSave);
      closeModal();
    } catch (error) {
      logError(ERROR_TYPE.UserActionFailed, {
        action: "saveMySizeProfile",
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const isTrustedCloseMessage =
        event.data === "sizeGuides-closeButton" &&
        event.source !== null &&
        trustedMessageOrigins().has(event.origin);

      if (isTrustedCloseMessage) {
        closeModal();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [closeModal, trustedMessageOrigins]);

  const selectedBrandData = contextBrands.find(
    (b) => b.brandName === formState.selectedBrand,
  );

  const selectedMapping = selectedBrandData?.sizeMappings.find(
    (sm) => sm.brandSize === formState.brandSize,
  );
  const brandSizePlaceholder = getTranslation(
    "pdp_my_size_profile_onboarding_brand_size_title",
  ).toLocaleUpperCase();
  const legLengthPlaceholder = getTranslation(
    "pdp_my_size_profile_onboarding_length_header",
  ).toLocaleUpperCase();
  const showLegDropdown =
    !!formState.brandSize && selectedMapping?.hasLegLength === false;
  const canCreateProfile =
    !loading &&
    !!formState.selectedBrand &&
    !!formState.brandSize &&
    (!showLegDropdown || !!formState.legLengthValue);
  const ctaTranslationKey = mySizeProfile
    ? "pdp_my_size_profile_edit_link"
    : "pdp_my_size_profile_onboarding_cta";

  return (
    <Modal
      modalType={MODAL_TYPE.SIDE}
      modalId="SizeHelpModal"
      title={getTranslation("pdp_my_size_profile_onboarding_title")}
      open={open}
      contentLoaded={true}
      closeModal={closeModal}
      focusOnClose={focusOnCloseRef.current}
      withScrollBar={false}
    >
      <div className={styles.container}>
        <div className={styles.mySizeProfile}>
          <p className={styles.intro}>
            {getTranslation("pdp_my_size_profile_onboarding_text")}
          </p>

          <div className={styles.brandGrid} role="group">
            {contextBrands.map((brand) => (
              <button
                type="button"
                key={brand.brandName}
                className={`${styles.brandButton} ${
                  formState.selectedBrand === brand.brandName
                    ? styles.brandButtonSelected
                    : ""
                }`}
                onClick={() => handleSelectBrand(brand.brandName)}
                aria-pressed={formState.selectedBrand === brand.brandName}
                aria-label={brand.brandName}
              >
                <img
                  className={styles.brandLogo}
                  src={brand.brandImage ?? undefined}
                  alt={brand.brandName}
                />
              </button>
            ))}
          </div>

          {showLegDropdown && (
            <div className={styles.legLengthSection}>
              <div className={styles.legLengthWarning} role="alert">
                <span
                  className={styles.legLengthWarningIcon}
                  aria-hidden="true"
                >
                  &#9432;
                </span>
                <p className={styles.legLengthWarningText}>
                  {getTranslation(
                    "pdp_my_size_profile_onboarding_brand_leg_length_message",
                  )}
                </p>
              </div>
            </div>
          )}

          <div className={styles.brandSizeSection}>
            <div
              className={`${styles.sizeSelects} ${
                showLegDropdown ? styles.sizeSelectsTwo : ""
              }`}
            >
              <select
                key={`brand-size-${formState.selectedBrand}`}
                className={styles.brandSizeSelect}
                value={formState.brandSize}
                disabled={!formState.selectedBrand}
                onChange={(e) => {
                  const newBrandSize = e.target.value;
                  updateFormState({
                    brandSize: newBrandSize,
                    legLengthValue: "",
                  });
                }}
                aria-label={getTranslation(
                  "pdp_my_size_profile_onboarding_brand_size_title",
                )}
                data-testid="waist-size-select"
              >
                <option value="" disabled>
                  {brandSizePlaceholder}
                </option>
                {(() => {
                  const validSizeProfileIdRegex = /^[A-Z]{2}\d+(\s+L\d+)?$/;
                  const options =
                    selectedBrandData?.sizeMappings
                      ?.filter(
                        (sm) =>
                          sm.sizeProfileId &&
                          validSizeProfileIdRegex.test(sm.sizeProfileId),
                      )
                      ?.map((sm, index) => (
                        <option
                          key={`size-option-${index}`}
                          value={sm.brandSize}
                        >
                          {sm.brandSize}
                        </option>
                      )) || [];
                  return options;
                })()}
              </select>
              {showLegDropdown && (
                <select
                  className={styles.brandSizeSelect}
                  value={formState.legLengthValue}
                  onChange={(e) => {
                    const newLegLength = e.target.value;
                    updateFormState({
                      legLengthValue: newLegLength,
                    });
                  }}
                  aria-label={getTranslation(
                    "pdp_my_size_profile_onboarding_length_header",
                  )}
                  data-testid="leg-length-select"
                >
                  <option value="" disabled>
                    {legLengthPlaceholder}
                  </option>
                  {contextLegLengths.map((ll, index) => (
                    <option key={`leg-length-${index}`} value={String(ll.size)}>
                      {ll.sizeName}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <Button
            handleClick={handleCreateSizeProfile}
            type={BUTTON_TYPES.primaryMySizeProfile}
            additionalClasses={styles.ctaButton}
            disabled={!canCreateProfile}
            dataTestId="create-size-profile"
          >
            {getTranslation(ctaTranslationKey)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
